import React, {useCallback} from 'react';

const TicketCard = ({
  ticket,
  quantity,
  updateQuantity,
  vehicleCapacity = 5, // Default capacity of 5 passengers per vehicle
  removeTicket,
  theme,
  isScheduled
}) => {

  // Theme color mapping
  const themeColors = {
    pink: {
      border: 'border-pink-primary-500',
      text: 'text-pink-primary-500',
      bg: 'bg-pink-primary-500',
    },
    blue: {
      border: 'border-blue-primary-500',
      text: 'text-blue-primary-500',
      bg: 'bg-blue-primary-500',
    },
    default: {
      border: 'border-gray-300',
      text: 'text-gray-800',
      bg: 'bg-gray-800',
    }
  };

  const colors = themeColors[theme] || themeColors.default;

  // this can only be taken into for scheduled ticket
  const isIncrementDisabled = isScheduled === true ? quantity >= ticket.available_quantity : false;

  // Generate vehicle boxes based on total vehicles and capacity
  const generateVehicleBoxes = useCallback(() => {
    if (quantity === 0) return null;

    // Calculate how many vehicles will be used based on quantity
    const vehiclesNeeded = Math.ceil(quantity / vehicleCapacity);
    // Use the provided totalVehicles or calculate based on quantity if not provided
    const vehiclesToShow =  Math.ceil(ticket.available_quantity / vehicleCapacity)

    const boxes = [];

    for (let i = 0; i < vehiclesToShow; i++) {
      // Calculate how many passengers in this vehicle
      const passengersInVehicle = i < vehiclesNeeded
        ? Math.min(vehicleCapacity, quantity - (i * vehicleCapacity))
        : 0; // Empty for unused vehicles

      // Calculate fill percentage
      const fillPercentage = (passengersInVehicle / vehicleCapacity) * 100;

      // Determine vehicle status
      const isUsed = i < vehiclesNeeded;
      const isFull = passengersInVehicle === vehicleCapacity;

      boxes.push(
        <div key={i} className="relative flex flex-col items-center">
          {/* Vehicle box */}
          <div className={`relative w-10 h-6 border rounded overflow-hidden ${
            isUsed
              ? `${colors.border} border-opacity-50`
              : `${colors.border} border-opacity-20`
          }`}>
            {/* Fill level */}
            <div
              className={`absolute bottom-0 left-0 w-full ${
                isUsed
                  ? isFull
                    ? colors.bg
                    : `${colors.bg} bg-opacity-80`
                  : 'bg-white bg-opacity-20'
              }`}
              style={{ height: `${fillPercentage}%` }}
            ></div>

            {/* Passenger count */}
            <div className="absolute inset-0 flex items-center justify-center text-xs">
              {isUsed ? passengersInVehicle : '-'}
            </div>
          </div>

          {/* Vehicle number indicator */}
          {vehiclesToShow > 1 && (
            <span className={`text-xs mt-1 ${
              isUsed ? colors.text : `${colors.text} text-opacity-40`
            }`}>#{i+1}</span>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2 mt-3 justify-center">
        {boxes}
      </div>
    );
  }, [quantity, ticket]);

  return (
    <div className="bg-white shadow-lg w-full">
      <div className={`ticket-card bg-white ${colors.border} border-2 border-solid`} styyle={{backgroundColor: ticket.ticket_type.color_code}} data-ticket-id={ticket.id}>
        <div className="p-4 flex flex-col flex-grow">

          <div className="flex justify-between items-start mb-2">
            <h3 className={`text-lg font-semibold ${colors.text}`}>{ticket.name}</h3>
            <button
              onClick={() => removeTicket(ticket.id)}
              className={`${colors.text} opacity-70 hover:opacity-100 focus:outline-none`}
              aria-label="Remove ticket"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/*  <p className="text-sm text-pink-secondary-500 mb-4" dangerouslySetInnerHTML={{__html: ticket.description}} />*/}
          <div className="mt-auto">
            <div className="flex justify-between items-center mb-2">

              <div>
                <span className={`text-xs ${colors.text} opacity-75`}>Price</span>
                <p className={`text-xl font-bold ${colors.text}`}>${ticket.price}</p>
              </div>

            {ticket.ticket_type.is_scheduled &&
              <div>
                <span className={`text-xs ${colors.text} opacity-75`}>Time</span>
                <p className={`text-xl font-bold ${colors.text}`}>{ticket?.timeslot?.formatted_time}</p>
              </div>
              }
              <div className="flex flex-col items-end">
                <span className={`bg-white bg-opacity-20 ${colors.text} px-2 py-1 text-xs`}>
                  {ticket.location}
                </span>

                <span className={`text-xs ${colors.text} opacity-75 mt-1`}>
                  {isScheduled === true ? <>Available: {ticket.available_quantity}</> : <span></span>}
                </span>
              </div>

            </div>

            {/* Vehicle allocation visualization */}
            {(quantity > 0 && ticket.ticket_type.is_scheduled) && (
              <div className="mt-3 border-t border-white border-opacity-20 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs ${colors.text} opacity-75`}>
                    Max {vehicleCapacity} per gondola. Larger groups will be split across multiple gondolas.
                  </span>
                  <span className={`text-xs ${colors.text}`}>
                    Using {Math.ceil(quantity / vehicleCapacity)} of {Math.ceil(ticket.available_quantity / vehicleCapacity)} Gondola{Math.ceil(ticket.available_quantity / vehicleCapacity) > 1 ? 's' : ''}
                  </span>
                </div>
                {generateVehicleBoxes()}
              </div>
            )}

            {/* Quantity Selector */}
            <div className="flex items-center space-x-2">
              <label className={`text-xs ${colors.text} opacity-75`}>Qty:</label>
              <div className={`flex border ${colors.border} border-opacity-30`}>
                <button
                  type="button"
                  onClick={() => updateQuantity(-1)}
                  className={`w-8 h-8 flex items-center justify-center ${colors.text} hover:bg-white hover:bg-opacity-10 focus:outline-none`}
                >
                  <svg className="w-3 h-3 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M20 12H4"/>
                  </svg>
                </button>
                <span
                  id={`quantity-${ticket.id}`}
                  className={`w-8 h-8 flex items-center justify-center ${colors.text} text-sm`}
                >
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => updateQuantity(1)}
                  disabled={isIncrementDisabled}
                  className={`w-8 h-8 flex items-center justify-center ${colors.text} hover:bg-white hover:bg-opacity-10 focus:outline-none ${isIncrementDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-3 h-3 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketCard;